/* eslint-disable */
import React, { forwardRef, useRef, useState, useEffect, useImperativeHandle, useMemo, useCallback } from 'react';
import cls from 'classnames';
import isEqual from '@dcode/is/equal/equal';
import isString from '@dcode/is/string/string';
import TextAnimChildren from './TextAnimChildren';
import { createStaggerClass } from './utils/stagger';
import Vendor from './utils/Vendor';

const usePrevious = (value) => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

const useEqual = (value) => {
	const previousValue = usePrevious(value);
	return isEqual(previousValue, value);
};

const useDifferent = (value) => {
	return !useEqual(value);
};

const usePristine = (value) => {
	const previousValue = usePrevious(value);
	return previousValue === undefined;
};

const useDirty = (value) => {
	const previousValue = usePrevious(value);
	return previousValue !== undefined;
};

export const TextAnim = forwardRef(({
	className,
	classes,
	useCssAnimation,
	stagger,
	enter,
	leave,
	onEnterChar,
	onLeaveChar,
	onEnter,
	onLeave,
	children,
	dangerouslySetInnerHTML,
	...props
}, ref) => {
	const disposeEnter = useRef();
	const disposeLeave = useRef();
	const childrenRef = useRef();
	const [cssEnter] = useState(isString(enter) && (Vendor.transitionEnd || Vendor.animationEnd));
	const [cssLeave] = useState(isString(leave) && (Vendor.transitionEnd || Vendor.animationEnd));
	const [innerHTML, setInnerHTML] = useState(dangerouslySetInnerHTML);
	const [innerText, setInnerText] = useState(children);
	const isHtmlDifferent = useDifferent(dangerouslySetInnerHTML);
	const isTextDifferent = useDifferent(children);
	const isHtmlPristine = usePristine(dangerouslySetInnerHTML);
	const isTextPristine = usePristine(children);

	const changeText = () => {
		const html = dangerouslySetInnerHTML && dangerouslySetInnerHTML.__html;
		if (html && isHtmlDifferent) setInnerHTML(dangerouslySetInnerHTML);
		else if (children && isTextDifferent) setInnerText(children);
	};

	const onEnterBlock = (element, index, list) => {
		Vendor.removeClass(element, enter);
		onEnterChar(element, index, list);
	};

	const onLeaveBlock = (element, index, list) => {
		Vendor.removeClass(element, leave);
		onLeaveChar(element, index, list);
	};

	const onEnterComplete = () => {
		if (disposeEnter.current) disposeEnter.current();
		onEnter();
	};

	const onLeaveComplete = () => {
		if (disposeLeave.current) disposeLeave.current();
		onLeave();
		changeText();
	};

	const triggerEnterBlock = (element) => {
		Vendor.addClass(element, enter);
	};

	const triggerLeaveBlock = (element) => {
		Vendor.addClass(element, leave);
	};

	const createEnterAnimation = () => {
		return () => {};
	};

	const createEnterAnimationCss = () => {
		const staggerClass = createStaggerClass(Vendor.animationEnd, childrenRef.current.chars);
		return staggerClass(onEnterBlock, onEnterComplete, triggerEnterBlock, stagger);
	};

	const createEnterTransitionCss = () => {
		const staggerClass = createStaggerClass(Vendor.transitionEnd, childrenRef.current.chars);
		return staggerClass(onEnterBlock, onEnterComplete, triggerEnterBlock, stagger);
	};

	const createLeaveAnimation = () => {
		return () => {};
	};

	const createLeaveAnimationCss = () => {
		const staggerClass = createStaggerClass(Vendor.animationEnd, childrenRef.current.chars);
		return staggerClass(onLeaveBlock, onLeaveComplete, triggerLeaveBlock, stagger);
	};

	const createLeaveTransitionCss = () => {
		const staggerClass = createStaggerClass(Vendor.transitionEnd, childrenRef.current.chars);
		return staggerClass(onLeaveBlock, onLeaveComplete, triggerLeaveBlock, stagger);
	};

	const disposeEnterAnimation = () => {
		if (disposeEnter.current) disposeEnter.current();
		disposeEnter.current = null;
	};

	const disposeLeaveAnimation = () => {
		if (disposeLeave.current) disposeLeave.current();
		disposeLeave.current = null;
	};

	const disposeAnimations = () => {
		disposeEnterAnimation();
		disposeLeaveAnimation();
	};

	const animateEnter = () => {
		if (disposeEnter.current) disposeEnter.current();
		if (cssEnter && useCssAnimation) disposeEnter.current = createEnterAnimationCss();
		else if (cssEnter) disposeEnter.current = createEnterTransitionCss();
		else disposeEnter.current = createEnterAnimation();
		return disposeAnimations;
	};

	const animateLeave = () => {
		if (disposeLeave.current) disposeLeave.current();
		if (cssEnter && useCssAnimation) disposeLeave.current = createLeaveAnimationCss();
		else if (cssLeave) disposeLeave.current = createLeaveTransitionCss();
		else disposeLeave.current = createLeaveAnimation();
		return disposeLeaveAnimation;
	};

	// useEffect(() => {
	// 	// console.log(!isHtmlPristine && isHtmlDifferent)
	// 	if (isHtmlPristine) animateEnter();
	// 	if (!isHtmlPristine && isHtmlDifferent) animateLeave();
	// });

	// useImperativeHandle(ref, () => {
	// 	// animateEnter();
	// 	return childrenRef.current;
	// });

	// useEffect(animateLeave, [dangerouslySetInnerHTML]);
	// useEffect(animateLeave, [children]);
	// useEffect(animateEnter, []);
	return (
		<TextAnimChildren
			className={className}
			classes={{
				textAnimChildren: cls(classes.textAnimChildren, classes.textAnim),
				textAnimChildrenRow: cls(classes.textAnimChildrenRow, classes.textAnimRow),
				textAnimChildrenChar: cls(classes.textAnimChildrenChar, classes.textAnimChar),
			}}
			dangerouslySetInnerHTML={dangerouslySetInnerHTML}
			children={innerText}
			ref={childrenRef}
			{...props}
		/>
	);
});

export default TextAnim;

// #0
// (x) - Preciso garantir que troque o texto apenas quando houver mudança de fato
// ( ) - O primeiro render E o setText animam a entrada <--- ponto de atenção
// ( ) - A mudança em ... anima a saída <--- ponto de atenção

// #1
// ( ) - adicionar transição de saída do current
// ( ) - adicionar transição de entrada de nextItem

// #2
// ( ) - ao acabar a animação de saída do current, remove a classe current e remove também a classe de animação de saída
// ( ) - ao acabar a animação de entrada do nextItem, adiciona a classe current e remove a classe de animação de entrada
